/* NeuralX Aura — native sizing/recommendation funnel
 * Ported from the standalone Next.js funnel (lib/quiz-logic.ts + quiz steps)
 * so the whole flow runs inside the Shopify theme — no Vercel, no DNS changes.
 *
 * Works as:
 *   - a full page  (templates/page.quiz.liquid → renders snippets/quiz-funnel)
 *   - an overlay    (snippets/quiz-overlay, opened by any [data-quiz-open])
 *
 * Each [data-quiz] root is an independent instance. State persists in
 * sessionStorage so a reopen keeps the user's answers. The result adds the
 * recommended ring + size to the cart and applies the first-buyer credit via
 * Shopify's /discount permalink. Falls back to the PDP if a size variant
 * can't be matched.
 */
(() => {
  "use strict";

  /* ---------- recommendation logic (mirrors quiz-logic.ts) ---------- */
  // NOTE (operator): create a $20 discount code named exactly this in
  // Shopify Admin → Discounts before launch, or change CREDIT_CODE to match.
  const CREDIT_CODE = "FIRST20";
  const CREDIT_NOTE = "$20 off · expires in 7 days";

  // Goal → model. Ordered so capability and price ascend together (no inversion).
  const USECASE_SKU = {
    essentials: "aura-ring",      // Aura Ring         · $239.99
    everyday:   "aura-ring-pro",  // Aura Ring X       · $279
    recovery:   "aura-ring-x",    // Aura Ring Pro     · $299
    training:   "aura-ring-pro-x" // Aura Ring Athlete · $334.99
  };

  const USECASE_RATIONALE = {
    essentials: "You want the essentials done well. Aura Ring covers sleep, recovery, and readiness at the best price — nothing you won't use.",
    everyday:   "You want a refined daily ring. Aura Ring X keeps the same core sensors with a better finish and longer battery.",
    recovery:   "You want deeper recovery insight. Aura Ring Pro adds multi-channel sensing and a sharper readiness model.",
    training:   "You train and want a real performance signal. Aura Ring Athlete weighs HRV trend and sleep quality the way an athlete needs."
  };

  const WHY = {
    "aura-ring": [
      "Same core sleep, recovery, and HRV signals as the higher tiers, at the lowest entry point.",
      "One readiness number, three contributors, one sentence — the cleanest daily app of any tracker.",
      "If you're new to ring tracking, this is the cleanest start."
    ],
    "aura-ring-pro": [
      "A more refined finish for daily wear and longer 10-day battery.",
      "Same daily sensors as Aura Ring, plus a tighter inner sensor seat.",
      "No subscription. Same 30-day return."
    ],
    "aura-ring-x": [
      "Multi-channel PPG for cleaner overnight heart rate.",
      "Daily readiness score with a one-line decision prompt.",
      "Sharper HRV trend and overnight temperature signal."
    ],
    "aura-ring-pro-x": [
      "Recovery score weighted toward sleep quality and HRV trend.",
      "HR-zone time tracking that complements your sport watch.",
      "Skin-temperature trend to flag overload and under-recovery early."
    ]
  };

  // Default colorway shown for each recommendation (matches the standalone funnel).
  const SKU_COLOR = {
    "aura-ring": "black",
    "aura-ring-pro": "silver",
    "aura-ring-x": "black",
    "aura-ring-pro-x": "rose"
  };

  function recommend(a) {
    const useCase = a.useCase || "essentials";
    const knownSize = typeof a.knownSize === "number" ? a.knownSize : null;
    const skuKey = USECASE_SKU[useCase] || "aura-ring";
    return {
      size: knownSize,               // null when the buyer doesn't know it yet
      uncertain: knownSize == null,
      skuKey,
      rationale: USECASE_RATIONALE[useCase] || USECASE_RATIONALE.essentials,
      why: WHY[skuKey] || WHY["aura-ring"],
      color: SKU_COLOR[skuKey] || "black",
      creditCode: CREDIT_CODE,
      creditNote: CREDIT_NOTE
    };
  }

  /* ---------- small helpers ---------- */
  const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const checkIcon =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';

  function money(cents, fallback) {
    if (typeof cents !== "number") return fallback || "";
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: (window.theme && window.theme.shop && window.theme.shop.currency) || "USD"
      }).format(cents / 100);
    } catch (e) { return "$" + (cents / 100).toFixed(2); }
  }

  function pickVariantId(prod, size) {
    if (!prod || !Array.isArray(prod.variants)) return null;
    const target = String(size);
    // Prefer an available variant whose option values include the size.
    let match = prod.variants.find(v => v.available && (v.options || []).map(String).includes(target));
    if (!match) match = prod.variants.find(v => (v.options || []).map(String).includes(target));
    return match ? match.id : null;
  }

  /* ---------- per-instance controller ---------- */
  function initQuiz(root) {
    if (root.__quizInit) return;
    root.__quizInit = true;

    const STORAGE_KEY = "aura-quiz-state";
    const TOTAL = 3; // steps that count toward the progress bar (1..3); result is step 4

    let answers = {};
    try { answers = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}") || {}; } catch (e) { answers = {}; }
    const save = () => { try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(answers)); } catch (e) {} };

    const $  = (s) => root.querySelector(s);
    const $$ = (s) => Array.from(root.querySelectorAll(s));

    const steps = $$("[data-step]");
    const bar = $("[data-quiz-bar]");
    const backBtn = $("[data-quiz-back]");

    // Variant / product data emitted by Liquid. The <script data-quiz-data> is a
    // SIBLING of [data-quiz] (not a child), so search the parent scope, not root.
    let DATA = {};
    const dataEl = (root.parentNode || document).querySelector("[data-quiz-data]")
                 || document.querySelector("[data-quiz-data]");
    if (dataEl) { try { DATA = JSON.parse(dataEl.textContent); } catch (e) { DATA = {}; } }

    let current = 1;

    function showStep(n) {
      current = n;
      steps.forEach(s => { s.hidden = Number(s.dataset.step) !== n; });
      if (bar) bar.style.width = Math.min(100, (Math.min(n, TOTAL) / TOTAL) * 100) + "%";
      if (backBtn) backBtn.hidden = n <= 1 || n > TOTAL;
      const active = steps.find(s => Number(s.dataset.step) === n);
      if (active) {
        const focusable = active.querySelector("input, button, [tabindex]");
        if (focusable && n > 1) {
          try { focusable.focus({ preventScroll: true }); } catch (e) {}
        }
        // Keep the active step in view inside the scroll container.
        if (!reduceMotion) active.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
      if (n === TOTAL + 1) renderResult();
    }

    function setAnswer(field, value) {
      answers[field] = value;
      save();
      // reflect pressed state on any matching option in this instance
      $$('[data-field="' + field + '"]').forEach(b => {
        b.setAttribute("aria-pressed", String(b.dataset.value === String(value)));
      });
    }

    function nextOf(n) { return n < TOTAL ? n + 1 : TOTAL + 1; }

    /* option buttons (finger, hand, use-case, "help me measure") */
    $$('.quiz__opt[data-field]').forEach(btn => {
      btn.addEventListener("click", () => {
        const field = btn.dataset.field;
        let value = btn.dataset.value;
        if (field === "knownSize" && value === "unknown") value = "unknown";
        setAnswer(field, value);
        if (btn.hasAttribute("data-advance")) showStep(nextOf(current));
      });
    });

    /* "Start" on the welcome step */
    $$("[data-quiz-start]").forEach(b => b.addEventListener("click", () => showStep(2)));

    /* known-size numeric path */
    const sizeInput = $("[data-quiz-size]");
    const sizeUse = $("[data-quiz-size-use]");
    if (sizeUse && sizeInput) {
      sizeUse.addEventListener("click", () => {
        const n = parseInt(sizeInput.value, 10);
        if (n >= 6 && n <= 13) {
          setAnswer("knownSize", n);
          showStep(nextOf(current));
        } else {
          sizeInput.focus();
          sizeInput.setAttribute("aria-invalid", "true");
        }
      });
      sizeInput.addEventListener("input", () => sizeInput.removeAttribute("aria-invalid"));
      sizeInput.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); sizeUse.click(); } });
    }

    /* email step → result */
    const emailForm = $("[data-quiz-email-form]");
    if (emailForm) {
      emailForm.addEventListener("submit", (e) => {
        e.preventDefault();
        const input = emailForm.querySelector('input[type="email"]');
        const email = (input && input.value || "").trim();
        if (!email) { if (input) input.focus(); return; }
        answers.email = email; save();
        // Best-effort lead capture via Shopify's customer contact form. Never blocks the result.
        try {
          const body = new URLSearchParams();
          body.set("form_type", "customer");
          body.set("utf8", "✓");
          body.set("contact[email]", email);
          body.set("contact[tags]", "quiz-lead");
          fetch("/contact", { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body }).catch(() => {});
        } catch (e2) {}
        showStep(TOTAL + 1);
      });
    }

    /* back */
    if (backBtn) backBtn.addEventListener("click", () => { if (current > 1) showStep(current - 1); });

    /* ---------- result ---------- */
    function renderResult() {
      const r = recommend(answers);
      const prod = DATA[r.skuKey] || {};
      const title = prod.title || "Aura Ring";
      const priceStr = money(prod.price, "$239.99");
      const handle = prod.handle || r.skuKey;

      const set = (sel, txt) => { const el = $(sel); if (el) el.textContent = txt; };
      set("[data-result-title]", title);
      set("[data-result-price]", priceStr);
      set("[data-result-size]", r.uncertain ? "—" : String(r.size));
      set("[data-result-code]", r.creditCode);
      set("[data-result-code-note]", r.creditNote);
      set("[data-result-rationale]", r.rationale);

      const img = $("[data-result-img]");
      if (img) {
        if (prod.image) img.src = prod.image;
        img.alt = title;
      }

      const whyList = $("[data-result-why]");
      if (whyList) {
        whyList.innerHTML = "";
        r.why.forEach(line => {
          const li = document.createElement("li");
          li.innerHTML = checkIcon + "<span>" + line + "</span>";
          whyList.appendChild(li);
        });
      }

      const uncertainNote = $("[data-result-uncertain]");
      if (uncertainNote) uncertainNote.hidden = !r.uncertain;

      // Primary CTA. With a known size: add that variant to cart + apply credit +
      // go to checkout. Without a size: don't guess — store the credit and send
      // them to the sizing guide so they buy the right size (fewer returns).
      const cta = $("[data-quiz-cta]");
      if (cta) {
        const ctaLabel = cta.querySelector("span");
        const discountUrl = "/discount/" + encodeURIComponent(r.creditCode);
        if (r.uncertain) {
          if (ctaLabel) ctaLabel.textContent = "Find my size first";
          cta.onclick = (e) => {
            e.preventDefault();
            window.location.href = discountUrl + "?redirect=/pages/sizing";
          };
        } else {
          if (ctaLabel) ctaLabel.textContent = "Add to cart — " + priceStr;
          const variantId = pickVariantId(prod, r.size);
          cta.onclick = async (e) => {
            e.preventDefault();
            cta.setAttribute("aria-busy", "true");
            if (variantId) {
              try {
                await fetch("/cart/add.js", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
                });
                window.location.href = discountUrl + "?redirect=/checkout";
                return;
              } catch (err) { /* fall through to PDP */ }
            }
            // Fallback: store the credit and send them to the PDP to add it.
            window.location.href = discountUrl + "?redirect=/products/" + encodeURIComponent(handle);
          };
        }
      }

      // Secondary CTA: "decide later" — confirm inline (email already captured).
      const later = $("[data-quiz-later]");
      const laterMsg = $("[data-quiz-later-msg]");
      if (later) {
        later.addEventListener("click", () => {
          if (laterMsg) { laterMsg.hidden = false; laterMsg.textContent = "Saved. Your size and code are on the way to " + (answers.email || "your inbox") + "."; }
        });
      }
    }

    /* restore pressed states from any saved answers, then start at step 1 */
    Object.keys(answers).forEach(f => {
      $$('[data-field="' + f + '"]').forEach(b => b.setAttribute("aria-pressed", String(b.dataset.value === String(answers[f]))));
    });
    if (sizeInput && typeof answers.knownSize === "number") sizeInput.value = answers.knownSize;
    showStep(1);

    // expose a reset for the overlay open
    root.__quizReset = () => showStep(1);
  }

  /* ---------- overlay open / close ---------- */
  function initOverlay() {
    const overlay = document.querySelector("[data-quiz-overlay]");
    if (!overlay) return;
    const panel = overlay.querySelector(".quiz-overlay__panel");
    let lastFocus = null;

    function open(e) {
      if (e) e.preventDefault();
      lastFocus = document.activeElement;
      overlay.classList.add("is-open");
      overlay.removeAttribute("hidden");
      document.body.style.overflow = "hidden";
      const quizRoot = overlay.querySelector("[data-quiz]");
      if (quizRoot && quizRoot.__quizReset) quizRoot.__quizReset();
      const close = overlay.querySelector("[data-quiz-close]");
      if (close) { try { close.focus(); } catch (e2) {} }
    }
    function close() {
      overlay.classList.remove("is-open");
      document.body.style.overflow = "";
      setTimeout(() => overlay.setAttribute("hidden", ""), 400);
      if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
    }

    // Any [data-quiz-open] (links/buttons) opens the overlay; href is the no-JS fallback.
    document.addEventListener("click", (e) => {
      const trigger = e.target.closest("[data-quiz-open]");
      if (trigger) open(e);
    });
    overlay.querySelectorAll("[data-quiz-close]").forEach(b => b.addEventListener("click", close));
    const scrim = overlay.querySelector(".quiz-overlay__scrim");
    if (scrim) scrim.addEventListener("click", close);
    document.addEventListener("keydown", (e) => { if (e.key === "Escape" && overlay.classList.contains("is-open")) close(); });

    // light focus trap
    overlay.addEventListener("keydown", (e) => {
      if (e.key !== "Tab" || !overlay.classList.contains("is-open")) return;
      const f = Array.from(overlay.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')).filter(el => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  function boot() {
    document.querySelectorAll("[data-quiz]").forEach(initQuiz);
    initOverlay();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
